import { memo, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader } from "ui/components/Loader";
import { NoResults } from "ui/components/NoResults";
import { formatCurrency, formatDateParam, useTranslate } from "helpers";
import { Routes as FinanceRoutes } from "ui/modules/finance/navigation";
import {
  getFleetVehicleInvoiceRentsAPI,
  getFleetVehicleInvoicesAPI,
  getInvoiceStatusesAPI,
} from "services/api/fleet/vehicles/invoices/api";
import { initialPagination } from "ui/components/Table/consts";
import {
  InvoiceOutgoingRow,
  InvoiceRentRow,
  InvoiceStatus,
} from "services/api/fleet/vehicles/invoices/types";
import {
  getInvoiceStatusBarFill,
  getInvoiceStatusRingColor,
  getInvoiceStatusAmountColor,
  invoiceStatusRank,
} from "services/api/invoices/consts";
import Sparkline from "../../../common/Sparkline";
import DashboardTooltip, { TooltipDivider, TooltipRow, TooltipTitle } from "../../../common/DashboardTooltip";
import BarTooltip, { BarTooltipRow, BarTooltipTitle } from "../../../common/HoverPopup";
import CardHeader from "../../../common/CardHeader";
import { primary, text } from "../../../common/dashboardPalette";
import { InvoiceStats, InvoiceStatusCategory } from "./types";
import { useDashboardDateRange } from "../../../../context/DashboardDateRangeContext";
import { inRange } from "services/api/dashboard/range";
import { rangeLabel } from "ui/components/DateRangePicker";

type InvoiceType = "sale" | "rent";

interface AggregatedInvoice {
  statusId: string;
  total: number;
  type: InvoiceType;
}

const InvoiceStatsTab: React.FC = () => {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const { range } = useDashboardDateRange();

  // Klik na status-red -> pretraga izlaznih računa po tom statusu + trenutnom datumskom rasponu widgeta.
  // Landa na tab "Izlazni" / podtab "Prodaja"; isti `status` param čita i podtab "Najam".
  const handleRowClick = (statusId: string) => {
    const params = new URLSearchParams({ tab: "outgoing", subtab: "sale", status: statusId });
    const from = formatDateParam(range.from);
    const to = formatDateParam(range.to);
    if (from) params.set("invoiceDateFrom", from);
    if (to) params.set("invoiceDateTo", to);
    navigate(`${FinanceRoutes.InvoiceSearchPageRouter}?${params.toString()}`);
  };

  const [stats, setStats] = useState<InvoiceStats>({
    totalCount: 0,
    totalValue: 0,
    totalSaleCount: 0,
    totalRentCount: 0,
    totalSaleValue: 0,
    totalRentValue: 0,
    avgValue: 0,
    inProgressCount: 0,
    inProgressValue: 0,
    monthlyTrend: [],
    categories: [],
  });
  const [isLoading, setIsLoading] = useState(true);

  const fetchInvoiceStats = async () => {
    try {
      setIsLoading(true);

      const [statusesResult, salesResult, rentsResult] = await Promise.all([
        getInvoiceStatusesAPI(),
        getFleetVehicleInvoicesAPI({ ...initialPagination, pageSize: 1000 }),
        getFleetVehicleInvoiceRentsAPI({ ...initialPagination, pageSize: 1000 }),
      ]);

      const statuses = statusesResult as InvoiceStatus[];
      // Filtriranje po globalnom datumskom rasponu (invoiceDate).
      const sales = salesResult.data.filter((i: InvoiceOutgoingRow) =>
        inRange(i.invoiceDate ? new Date(i.invoiceDate) : null, range));
      const rents = rentsResult.data.filter((i: InvoiceRentRow) =>
        inRange(i.invoiceDate ? new Date(i.invoiceDate) : null, range));

      const aggregated: (AggregatedInvoice & { date: Date | null })[] = [
        ...sales.map((i: InvoiceOutgoingRow) => ({
          statusId: i.status?.id || "unknown",
          total: i.total || 0,
          type: "sale" as InvoiceType,
          date: i.invoiceDate ? new Date(i.invoiceDate) : null,
        })),
        ...rents.map((i: InvoiceRentRow) => ({
          statusId: i.status?.id || "unknown",
          total: i.total || 0,
          type: "rent" as InvoiceType,
          date: i.invoiceDate ? new Date(i.invoiceDate) : null,
        })),
      ];

      const totalCount = aggregated.length;
      const totalValue = aggregated.reduce((sum, i) => sum + i.total, 0);
      const totalSaleCount = sales.length;
      const totalRentCount = rents.length;
      const totalSaleValue = sales.reduce((sum: number, i: InvoiceOutgoingRow) => sum + (i.total || 0), 0);
      const totalRentValue = rents.reduce((sum: number, i: InvoiceRentRow) => sum + (i.total || 0), 0);

      const grouped = aggregated.reduce(
        (acc: Record<string, { count: number; total: number; saleCount: number; rentCount: number; saleTotal: number; rentTotal: number }>, i) => {
          if (!acc[i.statusId]) {
            acc[i.statusId] = { count: 0, total: 0, saleCount: 0, rentCount: 0, saleTotal: 0, rentTotal: 0 };
          }
          acc[i.statusId].count += 1;
          acc[i.statusId].total += i.total;
          if (i.type === "sale") {
            acc[i.statusId].saleCount += 1;
            acc[i.statusId].saleTotal += i.total;
          } else {
            acc[i.statusId].rentCount += 1;
            acc[i.statusId].rentTotal += i.total;
          }
          return acc;
        },
        {}
      );

      const categories: InvoiceStatusCategory[] = Object.entries(grouped)
        .map(([statusId, data]) => {
          const status = statuses.find((s) => s.id === statusId);
          return {
            statusId,
            label: status?.name || statusId,
            count: data.count,
            total: data.total,
            percentage: totalCount > 0 ? Math.round((data.count / totalCount) * 100) : 0,
            color: status?.color || "blue",
            saleCount: data.saleCount,
            rentCount: data.rentCount,
            saleTotal: data.saleTotal,
            rentTotal: data.rentTotal,
          };
        })
        .sort((a, b) => invoiceStatusRank(a.statusId) - invoiceStatusRank(b.statusId));

      // U tijeku = računi koji nisu plaćeni ni otkazani.
      const inProgress = aggregated.filter((i) =>
        i.statusId !== "paid" && i.statusId !== "cancelled"
      );
      const inProgressCount = inProgress.length;
      const inProgressValue = inProgress.reduce((s, i) => s + i.total, 0);
      const avgValue = totalCount > 0 ? totalValue / totalCount : 0;

      // Mjesečni trend zadnjih 6 mjeseci.
      const now = new Date();
      const monthlyTrend = Array.from({ length: 6 }, (_, k) => {
        const monthStart = new Date(now.getFullYear(), now.getMonth() - (5 - k), 1);
        const monthEnd = new Date(now.getFullYear(), now.getMonth() - (5 - k) + 1, 1);
        return aggregated.filter((i) => i.date && i.date >= monthStart && i.date < monthEnd).length;
      });

      setStats({
        totalCount,
        totalValue,
        totalSaleCount,
        totalRentCount,
        totalSaleValue,
        totalRentValue,
        avgValue,
        inProgressCount,
        inProgressValue,
        monthlyTrend,
        categories,
      });
    } catch (error) {
      if ((error as any)?.response?.status !== 401) console.error("Error fetching invoice stats:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoiceStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.from, range.to]);

  if (isLoading) {
    return (
      <div className="relative h-full min-h-[200px]">
        <Loader isLoading={true}> </Loader>
      </div>
    );
  }

  if (stats.categories.length === 0) {
    return (
      <NoResults message={t("admin.dashboard.widget.invoices.stats.noresults")} />
    );
  }

  return (
    <div className="rounded-sm px-4 py-5 bg-white">
      <CardHeader
        title={t("admin.dashboard.widget.invoices.tab.stats.title")}
        subtitle={rangeLabel(range)}
        className="mb-10"
        right={
          <DashboardTooltip
            trigger={
              <div className="flex flex-col items-end cursor-help">
                <div className="flex items-center gap-2">
                  <Sparkline values={stats.monthlyTrend} stroke={primary.hex} fill={primary.hexSoft} />
                  <span className={`text-2xl font-bold ${text.primary}`}>
                    {formatCurrency.format(stats.totalValue)}
                  </span>
                </div>
                <span className={`text-sm ${text.muted}`}>⌀ {formatCurrency.format(stats.avgValue)} po računu</span>
              </div>
            }
          >
            <TooltipTitle>Pregled računa</TooltipTitle>
            <TooltipRow label="Prodaja" value={`${formatCurrency.format(stats.totalSaleValue)} (${stats.totalSaleCount})`} />
            <TooltipRow label="Najam" value={`${formatCurrency.format(stats.totalRentValue)} (${stats.totalRentCount})`} />
            <TooltipDivider />
            <TooltipRow label="Ukupno" value={`${formatCurrency.format(stats.totalValue)} (${stats.totalCount})`} emphasis="positive" />
            {stats.inProgressCount > 0 && (
              <TooltipRow label="U tijeku" value={`${stats.inProgressCount} · ${formatCurrency.format(stats.inProgressValue)}`} />
            )}
          </DashboardTooltip>
        }
      />

      <div className="space-y-2">
        {stats.categories.map((category) => (
          <div
            key={category.statusId}
            className={`group/row relative rounded-sm ring-1 ring-slate-200 bg-white cursor-pointer transition-all hover:ring-2 hover:shadow-sm ${getInvoiceStatusRingColor(category.statusId)}`}
            onClick={() => handleRowClick(category.statusId)}
          >
            {/* Ispuna proporcionalna udjelu — podaci stoje UNUTAR bara.
                overflow-hidden je na unutarnjem wrapperu da ne reže tooltip iznad reda. */}
            <div className="absolute inset-0 overflow-hidden rounded-sm">
              <div
                className={`absolute inset-y-0 left-0 origin-left animate-growRight transition-all duration-500 ${getInvoiceStatusBarFill(category.statusId)}`}
                style={{ width: `${Math.max(category.percentage, 5)}%` }}
              />
            </div>
            <div className="relative flex items-center justify-between gap-3 px-3 py-2">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-slate-800 truncate">{category.label}</div>
                <div className="text-xs text-slate-500 truncate">
                  {category.count} · {category.saleCount} prodaja / {category.rentCount} najam
                </div>
              </div>
              <div className="flex flex-col items-end flex-shrink-0">
                <span className={`text-sm font-bold ${getInvoiceStatusAmountColor(category.statusId)}`}>{formatCurrency.format(category.total)}</span>
                <span className="text-xs text-slate-500">{category.percentage}%</span>
              </div>
            </div>
            <BarTooltip groupName="row" align="right">
              <BarTooltipTitle>{category.label}</BarTooltipTitle>
              <BarTooltipRow label="Prodaja" value={`${category.saleCount} · ${formatCurrency.format(category.saleTotal)}`} />
              <BarTooltipRow label="Najam" value={`${category.rentCount} · ${formatCurrency.format(category.rentTotal)}`} />
            </BarTooltip>
          </div>
        ))}
      </div>

      <div className="mt-4 text-sm">
        <div className={text.muted}>
          {stats.totalCount}{" "}
          {t("admin.dashboard.widget.invoices.stats.totalinvoices")}
          {" · "}
          <span className={text.subtle}>{stats.totalSaleCount} prodaja / {stats.totalRentCount} najam</span>
        </div>
        {stats.inProgressCount > 0 && (
          <div className={`${primary.text} mt-1`}>
            <span className="font-medium">{stats.inProgressCount}</span> u tijeku
            <span className={text.subtle}>{" "}({formatCurrency.format(stats.inProgressValue)})</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default memo(InvoiceStatsTab);
